import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function Pillars() {
  // SEO Configuration
  useSEO({
    title: 'Seven Pillars - Faith, Family, Friends, Fitness, Finances, Food, Fun',
    description: 'Explore the seven pillars of family growth: Faith, Family, Friends, Fitness, Finances, Food, and Fun. A holistic framework for building micro-habits that transform your family life.',
    keywords: 'seven pillars, faith-based family, family fitness, family finances, family habits, holistic family growth, intentional parenting, family framework',
    canonical: '/pillars',
    schema: generateWebPageSchema({
      name: 'The Seven Pillars of Family Growth',
      description: 'A holistic framework for family growth. Each pillar supports the others, creating a balanced life rooted in what matters most.',
      url: '/pillars'
    })
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            The Seven Pillars
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            A holistic framework for family growth. Each pillar supports the others, creating a balanced life rooted in what matters most.
          </p>
        </div>
      </section>

      {/* Pillars Grid */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 gap-16">
            {pillars.map((pillar, index) => (
              <div 
                key={pillar.name}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
              >
                <div className="flex-1">
                  <div className={`inline-flex items-center space-x-3 px-4 py-2 ${pillar.bgColor} rounded-full mb-6`}>
                    <div className={`w-10 h-10 flex items-center justify-center ${pillar.iconBg} rounded-full`}>
                      <i className={`ri-${pillar.icon}-line text-xl`}></i>
                    </div>
                    <span className={`text-lg font-semibold ${pillar.textColor}`}>{pillar.name}</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {pillar.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {pillar.description}
                  </p>
                  <div className="space-y-3 mb-6">
                    <h4 className="font-semibold text-gray-900">Example Micro-Habits:</h4>
                    <ul className="space-y-2">
                      {pillar.habits.map((habit, idx) => (
                        <li key={idx} className="flex items-start">
                          <i className="ri-checkbox-circle-fill text-amber-500 mt-1 mr-3"></i>
                          <span className="text-gray-600">{habit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link 
                    to="/blog" 
                    className="inline-flex items-center px-6 py-3 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Read {pillar.name} Articles
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
                <div className="flex-1 relative h-96 w-full rounded-2xl overflow-hidden">
                  <img 
                    src={pillar.image}
                    alt={pillar.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            The Pillars Work Together
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            The seven pillars aren't isolated categories—they're interconnected aspects of a whole life. A morning prayer (Faith) with your family (Family) before a walk (Fitness) creates compound benefits. When you strengthen one pillar, you naturally support the others.
          </p>
          <p className="text-lg text-gray-600 mb-12 leading-relaxed">
            Start with one pillar, build momentum, then expand. Over time, you'll create a balanced rhythm that touches every area of family life.
          </p>
          <Link 
            to="/how-it-works" 
            className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-full font-medium hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Learn How to Get Started
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const pillars = [
  {
    name: "Faith",
    title: "Rooted in What Matters Most",
    description: "Faith is the foundation that grounds your family in purpose and meaning. Through simple spiritual practices, you create space for gratitude, reflection, and connection to something greater than yourselves.",
    icon: "heart",
    bgColor: "bg-amber-50",
    iconBg: "bg-amber-500 text-white",
    textColor: "text-amber-800",
    image: "https://public.readdy.ai/ai/img_res/6ab44718e5672dec58204bd9761ce94d.jpg",
    habits: [
      "2-minute morning prayer or gratitude practice",
      "Bedtime blessing ritual with each family member",
      "Weekly scripture reading or devotional discussion"
    ]
  },
  {
    name: "Family",
    title: "Building Bonds That Last",
    description: "Family is where transformation happens. Shared micro-habits create connection, communication, and memories that strengthen your bonds across generations. Small moments of intentional togetherness compound into a lifetime of closeness.",
    icon: "home-heart",
    bgColor: "bg-rose-50",
    iconBg: "bg-rose-500 text-white",
    textColor: "text-rose-800",
    image: "https://readdy.ai/api/search-image?query=multi%20generation%20family%20laughing%20together%20in%20cozy%20living%20room%20warm%20natural%20lighting%20genuine%20connection%20and%20joy%20simple%20home%20interior%20bonding%20moment%20lifestyle%20photography&width=800&height=600&seq=pillar2&orientation=landscape",
    habits: [
      "Daily family hug before leaving home",
      "Dinner table check-in: share highs and lows",
      "10-minute evening walk together"
    ]
  },
  {
    name: "Friends",
    title: "Cultivating Community",
    description: "Friendships enrich life and teach children the value of relationships beyond family. Micro-habits around friendship help you stay connected, show up for others, and model healthy relationships for your children.",
    icon: "group",
    bgColor: "bg-emerald-50",
    iconBg: "bg-emerald-500 text-white",
    textColor: "text-emerald-800",
    image: "https://readdy.ai/api/search-image?query=friends%20gathering%20together%20sharing%20meal%20and%20laughter%20warm%20inviting%20atmosphere%20natural%20lighting%20genuine%20connection%20community%20and%20friendship%20theme%20joyful%20moment&width=800&height=600&seq=pillar3&orientation=landscape",
    habits: [
      "Send one meaningful text to a friend each week",
      "Monthly family game night with another family",
      "Teach kids to write thank-you notes to friends"
    ]
  },
  {
    name: "Fitness",
    title: "Moving Together, Growing Stronger",
    description: "Physical health impacts every area of life. Family fitness micro-habits make movement fun, build healthy patterns early, and create energy for everything else you want to accomplish together.",
    icon: "run",
    bgColor: "bg-cyan-50",
    iconBg: "bg-cyan-500 text-white",
    textColor: "text-cyan-800",
    image: "https://readdy.ai/api/search-image?query=active%20family%20exercising%20together%20outdoors%20in%20park%20morning%20sunlight%20parents%20and%20children%20stretching%20and%20moving%20joyful%20healthy%20lifestyle%20natural%20setting%20energetic%20atmosphere&width=800&height=600&seq=pillar4&orientation=landscape",
    habits: [
      "5-minute family stretch after dinner",
      "Weekend nature walk or bike ride",
      "Dance party while cleaning up together"
    ]
  },
  {
    name: "Finances",
    title: "Teaching Stewardship &amp; Wisdom",
    description: "Financial habits shape your family's future and teach children valuable life skills. Small, consistent actions around money create security, generosity, and wisdom that last a lifetime.",
    icon: "money-dollar-circle",
    bgColor: "bg-violet-50",
    iconBg: "bg-violet-500 text-white",
    textColor: "text-violet-800",
    image: "https://readdy.ai/api/search-image?query=parent%20teaching%20children%20about%20money%20and%20savings%20at%20kitchen%20table%20piggy%20bank%20and%20coins%20warm%20home%20setting%20natural%20lighting%20financial%20education%20moment%20simple%20background&width=800&height=600&seq=pillar5&orientation=landscape",
    habits: [
      "Weekly family budget check-in (5 minutes)",
      "Gratitude jar: add $1 when sharing thanks",
      "Teach kids to divide allowance: save, spend, give"
    ]
  },
  {
    name: "Food",
    title: "Nourishing Body &amp; Soul",
    description: "Food is more than fuel—it's connection, tradition, and health. Micro-habits around food teach life skills, create memories, and establish patterns of nourishment that serve your family for years.",
    icon: "restaurant",
    bgColor: "bg-orange-50",
    iconBg: "bg-orange-500 text-white",
    textColor: "text-orange-800",
    image: "https://readdy.ai/api/search-image?query=family%20cooking%20healthy%20meal%20together%20in%20bright%20kitchen%20fresh%20vegetables%20on%20counter%20parents%20and%20children%20preparing%20food%20warm%20natural%20light%20joyful%20bonding%20moment%20lifestyle%20photography&width=800&height=600&seq=pillar6&orientation=landscape",
    habits: [
      "Try one new vegetable together each week",
      "Sunday meal prep as a family activity",
      "Everyone helps with one meal task daily"
    ]
  },
  {
    name: "Fun",
    title: "Creating Joy &amp; Memories",
    description: "Fun isn't frivolous—it's essential. Playfulness reduces stress, strengthens bonds, and creates the memories your children will treasure. Micro-habits of fun keep your family connected and joyful.",
    icon: "emotion-happy",
    bgColor: "bg-pink-50",
    iconBg: "bg-pink-500 text-white",
    textColor: "text-pink-800",
    image: "https://readdy.ai/api/search-image?query=family%20playing%20board%20game%20together%20laughing%20and%20having%20fun%20cozy%20home%20setting%20warm%20lighting%20genuine%20joy%20and%20connection%20playful%20atmosphere%20lifestyle%20photography&width=800&height=600&seq=pillar7&orientation=landscape",
    habits: [
      "10-minute game before bedtime",
      "Weekly family movie or show night",
      "Monthly adventure: try something new together"
    ]
  }
];